import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API, { ErrorResponse, PaginatedResponse } from '../../api/Api';
import Activity, { ActivitySearchParams } from '../../models/Activity';
import Institution, { InstitutionSearchParams } from '../../models/Institution';
import Syllabus, { CompatibleSyllabusSearchParams, SourceSuggestedSyllabus, SuggestedSyllabus, SyllabusSearchParams } from '../../models/Syllabus';
import Student from '../../models/Student';
import EquivalencyRequest, { EquivalencyRequestParams, EquivalencyRequestSimulationSearchParams, PaginatePrevEquivalencyResult, PreviousEquivalencyRequestParams, RequestById } from '../../models/EquivalencyRequest';
import { Simulation } from '../../models/Simulation';
import { EquivalencyRuleSearchParams } from '../../models/EquivalencyRule';
import EquivalencyRuleActivity from '../../models/EquivalencyRuleActivity';
import { HubspotContactParams, HubspotContactResponse } from '../../models/HubspotContact';
import DealData, { DealDataResponse } from '../../models/DealData';
import { Cardinality, CardinalityResponse } from '../../models/Cardinality';
import { StudentRecordsSyllabus, StudentRecordsSyllabusesParams } from '../../models/StudentRecord';

export const fetchSourceInstitutions = createAsyncThunk<PaginatedResponse<Institution>, { parameters: InstitutionSearchParams; signal?: AbortSignal }>(
  'request/fetchSourceInstitutions',
  async ({ parameters, signal }: { parameters: InstitutionSearchParams; signal?: AbortSignal }, thunkAPI) => {
    try {
      const response = await API.getInstitutions(parameters, signal);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchTargetInstitution = createAsyncThunk(
  'request/fetchTargetInstitution',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getTargetInstitution();
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchCardinality = createAsyncThunk<CardinalityResponse, { ruleIds: string[] }>(
  'request/fetchCardinality',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getCardinality(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchSourceSyllabuses = createAsyncThunk<PaginatedResponse<Syllabus>, SyllabusSearchParams>(
  'request/fetchSourceSyllabuses',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSyllabusInInstitution(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchTargetSyllabuses = createAsyncThunk<PaginatedResponse<Syllabus>, SyllabusSearchParams>(
  'request/fetchTargetSyllabus',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSyllabusInInstitution(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompatibilityTargetSyllabuses = createAsyncThunk<SourceSuggestedSyllabus, CompatibleSyllabusSearchParams>(
  'request/fetchCompatibilityTargetSyllabus',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSyllabusCompatibilityInInstitution(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Syllabus>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchSourceSyllabusActivities = createAsyncThunk<{ activities: Activity[] }, ActivitySearchParams>(
  'request/fetchSourceSyllabusActivities',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSyllabusActivities(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchTargetSyllabusActivities = createAsyncThunk<{ activities: Activity[] }, ActivitySearchParams>(
  'request/fetchTargetSyllabusActivities',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSyllabusActivities(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchStudentRecordsSyllabuses = createAsyncThunk<{ items: StudentRecordsSyllabus[] }, StudentRecordsSyllabusesParams>(
  'request/fetchStudentRecordsSyllabuses',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getStudentRecordsSyllabuses(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchSimulateEquivalencyRequest = createAsyncThunk<Simulation, EquivalencyRequestSimulationSearchParams>(
  'request/fetchSimulateEquivalencyRequest',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getSimulateEquivalencyRequest(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const searchPreviousEquivalencyRequest = createAsyncThunk<PaginatePrevEquivalencyResult, PreviousEquivalencyRequestParams>(
  'equivalency/searchRequest',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getPreviousEquivalencyRequest(parameters);
      return response
    } catch (err) {
      const error = err as ErrorResponse<EquivalencyRequest>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
)

export const fetchRequestById = createAsyncThunk<RequestById, string>(
  'request/fetchRequestById',
  async (requestId, thunkAPI) => {
    try {
      const response = await API.getRequestById(requestId);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<RequestById>;
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const saveStudent = createAsyncThunk<Student, Student>(
  'request/saveStudent',
  async (student, thunkAPI) => {
    try {
      const response: Student = await API.putStudent(student);
      return {
        ...response,
        city: student.city,
        // remove after mapping in the back
        email: student.email,
        telephone: student.telephone
      };
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const saveRequest = createAsyncThunk<EquivalencyRequest, EquivalencyRequestParams>(
  'request/saveRequest',
  async (params, thunkAPI) => {
    try {
      const response = await API.putRequest(params, false);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Institution>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchEquivalencyRuleActivity = createAsyncThunk<EquivalencyRuleActivity[], EquivalencyRuleSearchParams>(
  'fetchEquivalencyRuleActivity',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getEquivalencyRuleActivity(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Student>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchHubspotContact = createAsyncThunk<HubspotContactResponse, HubspotContactParams>(
  'fetchHubspotContact',
  async (body, thunkAPI) => {
    try {
      const response = await API.postHubspotContact(body);
      return response.data as HubspotContactResponse
    } catch (err) {
      const error = err as ErrorResponse<Student>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchHubspotDeal = createAsyncThunk<DealDataResponse, DealData>(
  'fetchHubspotDeal',
  async (body, thunkAPI) => {
    try {
      const response = await API.createDeal(body);
      return response
    } catch (err) {
      if (!err) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err);
    }
  }
);

interface RequestStore {
  selected: EquivalencyRequest | null
  sourceInstitutions: Institution[]
  sourceSyllabuses: Syllabus[]
  targetSyllabuses: Syllabus[]
  sourceActivities: Activity[],
  targetSyllabusActivities: Activity[],
  targetInstitution: Institution | null
  simulation: Simulation | null
  student: Student | null
  studentComplete: boolean
  isSuccess: boolean
  rules: EquivalencyRuleActivity[]
  compatibilityTargetSyllabuses: SuggestedSyllabus[],
  compatibilityFetched: boolean;
  contactId: string | null,
  requestById: RequestById | null,
  dealId: string,
  cardinality: Cardinality[]
  studentRecordsSyllabuses: StudentRecordsSyllabus[]
}

const initialState: RequestStore = {
  selected: null,
  sourceInstitutions: [],
  sourceSyllabuses: [],
  targetSyllabuses: [],
  sourceActivities: [],
  targetSyllabusActivities: [],
  targetInstitution: null,
  simulation: null,
  student: null,
  studentComplete: false,
  isSuccess: false,
  rules: [],
  compatibilityTargetSyllabuses: [],
  compatibilityFetched: false,
  contactId: null,
  requestById: null,
  dealId: '',
  cardinality: [],
  studentRecordsSyllabuses: []

};

const requestSlice = createSlice({
  name: 'request',
  initialState,
  reducers: {
    reset: (state) => {
      state.sourceInstitutions = [];
      state.sourceSyllabuses = [];
      state.targetSyllabuses = [];
      state.sourceActivities = [];
      state.targetSyllabusActivities = [];
      state.simulation = null;
      state.selected = null;
      state.student = null;
      state.studentComplete = false;
      state.rules = [];
      state.compatibilityTargetSyllabuses = [];
      state.contactId = null;
      state.requestById = null;
      state.dealId = '';
      state.cardinality = [];
      state.studentRecordsSyllabuses = []
    },
    resetSourceInstitutions: (state) => {
      state.sourceInstitutions = [];
    },
    resetSourceActivities: (state) => {
      state.sourceActivities = [];
    },
    resetTargetSyllabusActivities: (state) => {
      state.targetSyllabusActivities = [];
    },
    resetSourceSyllabuses: (state) => {
      state.sourceSyllabuses = [];
    },
    resetCompatibilityTargetSyllabuses: (state) => {
      state.compatibilityTargetSyllabuses = [];
      state.compatibilityFetched = false;
    },
    resetSimulateEquivalency: (state) => {
      state.simulation = null;
    },
    addSourceActiviy: (state, action) => {
      state.sourceActivities = [
        action.payload,
        ...state.sourceActivities
      ];
      state.isSuccess = false;
    },
    resetCardinality: (state) => {
      state.cardinality = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHubspotDeal.fulfilled, (state, action) => {
      state.dealId = action.payload.dealId;
    });

    builder.addCase(fetchHubspotContact.fulfilled, (state, action) => {
      state.contactId = action.payload.id ?? null;
    });

    builder.addCase(fetchTargetInstitution.fulfilled, (state, action) => {
      state.targetInstitution = action.payload ?? null;
    });

    builder.addCase(fetchRequestById.fulfilled, (state, action) => {
      state.requestById = action.payload ?? null;
    });
    
    builder.addCase(fetchCardinality.fulfilled, (state, action) => {
      state.cardinality = action.payload.cardinality ?? [];
    });

    builder.addCase(fetchSourceInstitutions.fulfilled, (state, action) => {
      state.sourceInstitutions = action.payload.items;
    });

    builder.addCase(fetchSourceSyllabuses.fulfilled, (state, action) => {
      state.sourceSyllabuses = action.payload.items;
    });

    builder.addCase(fetchTargetSyllabuses.fulfilled, (state, action) => {
      state.targetSyllabuses = action.payload.items;
    });

    builder.addCase(fetchCompatibilityTargetSyllabuses.fulfilled, (state, action) => {
      state.compatibilityTargetSyllabuses = action.payload.compatibilities;
    });

    builder.addCase(fetchCompatibilityTargetSyllabuses.pending, (state) => {
      state.compatibilityFetched = true;
    });

    builder.addCase(fetchStudentRecordsSyllabuses.fulfilled, (state, action) => {
      state.studentRecordsSyllabuses = action.payload.items;
    });


    builder.addCase(fetchSourceSyllabusActivities.fulfilled, (state, action) => {
      state.sourceActivities = action.payload.activities;
    });

    builder.addCase(fetchTargetSyllabusActivities.fulfilled, (state, action) => {
      state.targetSyllabusActivities = action.payload.activities;
    });

    builder.addCase(fetchSimulateEquivalencyRequest.fulfilled, (state, action) => {
      state.simulation = action.payload;
    });

    builder.addCase(saveStudent.fulfilled, (state, action) => {
      state.student = action.payload;
      state.isSuccess = !state.isSuccess;
      state.studentComplete = true;
    });

    builder.addCase(saveRequest.fulfilled, (state, action) => {
      state.selected = action.payload;
    });

    builder.addCase(fetchEquivalencyRuleActivity.fulfilled, (state, action) => {
      state.rules = action.payload;
    });
  }
});

export const { reset, addSourceActiviy, resetSimulateEquivalency, resetSourceActivities, resetSourceInstitutions, resetSourceSyllabuses, resetCompatibilityTargetSyllabuses, resetCardinality } = requestSlice.actions;

export default requestSlice.reducer;
